import { useState, useEffect, type FormEvent } from "react";
import { createPortal } from "react-dom";
import { Calendar, Check, X } from "lucide-react";
import { CustomDatePicker } from "./CustomDatePicker.tsx";

interface DateRangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (range: { startDate: string; endDate: string }) => void;
  initialStartDate?: string;
  initialEndDate?: string;
}

export function DateRangeModal({
  isOpen,
  onClose,
  onApply,
  initialStartDate,
  initialEndDate,
}: DateRangeModalProps) {
  const maxDate = getYesterdayStr();
  const [startDate, setStartDate] = useState(initialStartDate || getDefaultStart(7));
  const [endDate, setEndDate] = useState(initialEndDate || maxDate);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setStartDate(initialStartDate || getDefaultStart(7));
      setEndDate(initialEndDate || maxDate);
      setError(null);
    }
  }, [isOpen, initialStartDate, initialEndDate, maxDate]);

  if (!isOpen) return null;

  const applyPreset = (days: number) => {
    const start = getDefaultStart(days);
    setStartDate(start);
    setEndDate(maxDate);
    setError(null);
  };

  const applyThisMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const start = firstDay.toISOString().split("T")[0];
    setStartDate(start);
    setEndDate(maxDate);
    setError(null);
  };

  const applyLastMonth = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth(), 0);
    setStartDate(firstDay.toISOString().split("T")[0]);
    setEndDate(lastDay.toISOString().split("T")[0]);
    setError(null);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!startDate || !endDate) {
      setError("请选择完整的起始与结束日期");
      return;
    }
    const startMs = new Date(startDate).getTime();
    const endMs = new Date(endDate).getTime();
    if (startMs > endMs) {
      setError("起始日期不能晚于结束日期");
      return;
    }
    const days = Math.round((endMs - startMs) / (1000 * 60 * 60 * 24)) + 1;
    if (days > 365) {
      setError("自定义查询范围最大支持 365 天");
      return;
    }
    onApply({ startDate, endDate });
    onClose();
  };

  const modalContent = (
    <div
      className="dialog-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dialog date-range-dialog" role="dialog" aria-modal="true" aria-labelledby="date-modal-title">
        <div className="dialog-header">
          <div className="dialog-title-group">
            <Calendar className="dialog-title-icon" />
            <div>
              <h2 id="date-modal-title">自定义归因日期范围</h2>
              <small>支持选择历史数据区间，最长跨度 365 天</small>
            </div>
          </div>
          <button className="icon-button" onClick={onClose} title="关闭" aria-label="关闭对话框">
            <X />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="preset-section">
            <span className="preset-label">快捷预设：</span>
            <div className="preset-buttons">
              <button type="button" className="preset-btn" onClick={() => applyPreset(7)}>最近 7 天</button>
              <button type="button" className="preset-btn" onClick={() => applyPreset(14)}>最近 14 天</button>
              <button type="button" className="preset-btn" onClick={() => applyPreset(30)}>最近 30 天</button>
              <button type="button" className="preset-btn" onClick={() => applyPreset(60)}>最近 60 天</button>
              <button type="button" className="preset-btn" onClick={() => applyPreset(365)}>最近 1 年</button>
              <button type="button" className="preset-btn" onClick={applyThisMonth}>本月至今</button>
              <button type="button" className="preset-btn" onClick={applyLastMonth}>上月整月</button>
            </div>
          </div>

          <div className="date-inputs-grid">
            <div className="field">
              <span>起始日期 (Start Date)</span>
              <CustomDatePicker
                value={startDate}
                max={endDate || maxDate}
                onChange={(val) => {
                  setStartDate(val);
                  setError(null);
                }}
                required
              />
            </div>
            <div className="field">
              <span>结束日期 (End Date)</span>
              <CustomDatePicker
                value={endDate}
                min={startDate}
                max={maxDate}
                onChange={(val) => {
                  setEndDate(val);
                  setError(null);
                }}
                required
              />
            </div>
          </div>

          {error && <div className="date-modal-error">{error}</div>}

          <div className="dialog-actions">
            <button type="button" className="secondary-button" onClick={onClose}>
              取消
            </button>
            <button type="submit" className="primary-button">
              <Check size={16} />
              应用所选日期
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return typeof document !== "undefined" ? createPortal(modalContent, document.body) : modalContent;
}

function getYesterdayStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
}

function getDefaultStart(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split("T")[0];
}
